import { runInAction, action, computed, observable, makeObservable } from "mobx";
import { API } from "General/API";
import { projectsDataStore } from "./ProjectsStore";
import { download } from "utils/download";
import { API_URL } from "General/APIEndpoints";
import { handleStoreState, CoreStore } from "./RESTStore/CoreStore";
import { sortBy } from "lodash";
import i18n, { I18N_NS } from "Config/i18n";
import { formatUsername } from "utils/formatUsername";

export interface ContactWithFullName extends API_Contact {
  fullName?: string;
}

export interface UserWithFullName extends API_User {
  fullName?: string;
}

export class ContactsStore extends CoreStore {
  contacts: ContactWithFullName[] = [];
  users: UserWithFullName[] = [];
  showDeleted = false;

  constructor() {
    super();

    makeObservable(this, {
      contacts: observable,
      users: observable,
      showDeleted: observable,
      getUserById: action.bound,
      getUserNameById: action.bound,
      getContactById: action.bound,
      toggleShowDeleted: action.bound,
      getUsersList: action.bound,
      projectUsers: computed,
      allData: computed,
      userContacts: computed,
      globalUserContacts: computed,
      privateUserContacts: computed,
      tenderContacts: computed,
      pubAndCFT: computed,
      usersAndPublicContacts: computed,
      getVcard: action.bound,
      exportExcel: action.bound,
      createContact: action.bound,
      editContact: action.bound,
      fetch: action.bound,
      deleteContact: action.bound,
      restoreContact: action.bound,
      submit: action.bound,
    });
  }

  url(id?: number) {
    if (id) {
      return API_URL.CONTACTS.update(projectsDataStore.projectId, id);
    } else {
      return API_URL.CONTACTS.getContacts(projectsDataStore.projectId);
    }
  }

  clear() {
    this.contacts = [];
    this.users = [];
    this.showDeleted = false;
  }

  @handleStoreState()
  async fetch() {
    try {
      const [contacts, users] = await Promise.all([
        API.getData<API_Contact[]>(this.url()),
        API.getData<API_User[]>(API_URL.PROJECTS.getUsersByProjectId_1(projectsDataStore.projectId)),
      ]);
      runInAction(() => {
        this.users = users.map((item) => ({ ...item, fullName: formatUsername(item) }));
        this.contacts = contacts.map((item) => ({ ...item, fullName: item.name }));
      });
    } catch (error) {
      throw error;
    }
  }

  getUserById(userId: number): API_User {
    return this.projectUsers.find((item) => userId === item.id);
  }

  getUserByUserId(userId: string): API_User {
    return this.projectUsers.find((item) => userId === item.userId);
  }

  getUserNameByUserId(userId: string, returnFallback = true): string | null {
    const user = this.getUserByUserId(userId);
    if (!user) {
      if (returnFallback) {
        return i18n.t("[Unknown user]", { ns: I18N_NS.GENERAL });
      }
      return null;
    }
    return formatUsername(user);
  }

  getUserNameById(userId: number | string, returnFallback = true): string {
    const user = this.getUserById(+userId);
    if (!user) {
      if (returnFallback) {
        return i18n.t("[Removed user]", { ns: I18N_NS.GENERAL });
      }
      return null;
    }
    return formatUsername(user);
  }

  getContactById(contactId: number) {
    return this.userContacts.find((item) => contactId === item.id);
  }

  toggleShowDeleted() {
    this.showDeleted = !this.showDeleted;
  }

  async getUsersList() {
    if (this.state === "LOADING") {
      return;
    } else {
      this.fetch();
    }
  }

  /**
   * Use when need to manipulate with project users list.
   */
  get projectUsers() {
    return this.users.slice();
  }

  get activeProjectUsers() {
    return this.users.filter((item) => item.active);
  }

  get allData(): (API_Contact & API_User)[] {
    return this.contacts
      .filter((item) => item.active && item.contactType !== "CFT")
      .concat(this.projectUsers)
      .slice();
  }

  get userContacts() {
    return this.contacts.slice();
  }

  get globalUserContacts() {
    return this.contacts.filter((i) => i.contactType === "PUBLIC" && (this.showDeleted ? true : i.active));
  }

  get privateUserContacts() {
    return this.contacts.filter((i) => i.contactType === "PRIVATE" && (this.showDeleted ? true : i.active));
  }

  get tenderContacts() {
    return this.contacts.filter((i) => i.contactType === "CFT" && (this.showDeleted ? true : i.active));
  }

  get pubAndCFT() {
    const contacts = this.contacts.filter(
      (i) => (i.contactType === "CFT" || i.contactType === "PUBLIC") && (this.showDeleted ? true : i.active)
    );
    // DEV-5436 - show CFT contacts first
    return sortBy(contacts, ["contactType", "name"]);
  }

  get usersAndPublicContacts(): Array<ContactWithFullName | UserWithFullName> {
    const contacts = this.contacts.filter((item) => item.contactType === "PUBLIC");
    return contacts.concat(this.users.slice());
  }

  async getVcard(contactId: number, contactName: string, isContact: boolean) {
    try {
      const url = isContact
        ? API_URL.CONTACTS.getContactCard(projectsDataStore.projectId, contactId)
        : API_URL.USERS.getContactCard_1(projectsDataStore.projectId, contactId);
      const vCard = await API.getBlob(url);
      runInAction(() => {
        download(vCard, `${contactName}.vcf`, "text/v-card");
      });
    } catch (err) {
      throw err;
    }
  }

  async exportExcel() {
    try {
      const excel: File = await API.getBlob(API_URL.CONTACTS.exportContacts(projectsDataStore.projectId));
      runInAction(() => {
        download(excel, excel.name, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      });
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async deleteContact(id: number) {
    try {
      await API.deleteData(this.url(id));
      runInAction(() => {
        const deletedIndex = this.contacts.findIndex((item) => item.id === id);
        if (deletedIndex !== -1) {
          this.contacts[deletedIndex].active = false;
        }
      });
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async restoreContact(id: number) {
    try {
      const contactIdx = this.contacts.findIndex((item) => item.id === id);
      if (contactIdx === -1) {
        throw new Error(`Contact data for ID "${id}" not found`);
      }
      await API.editData(this.url(id), { active: true });
      runInAction(() => {
        this.contacts[contactIdx] = { ...this.contacts[contactIdx], active: true };
      });
      return { ...this.contacts[contactIdx] };
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState()
  async submit(formData: API_Contact) {
    try {
      if (formData.id) {
        await this.editContact(formData);
      } else {
        await this.createContact(formData);
      }
      this.fetch();
    } catch (error) {
      throw error;
    }
  }

  async createContact(formData: API_Contact) {
    try {
      const response = await API.newData(this.url(), formData);
      runInAction(() => {
        this.contacts.push(response);
      });
    } catch (error) {
      throw error;
    }
  }

  async editContact(formData: API_Contact) {
    try {
      await API.editData(this.url(formData.id), formData);
      runInAction(() => {
        const editIndex = this.contacts.findIndex((item) => item.id === formData.id);
        if (editIndex !== -1) {
          this.contacts[editIndex] = formData;
        }
      });
    } catch (error) {
      throw error;
    }
  }
}

export const contactsStore = new ContactsStore();
